import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { BarChart2, Eye, EyeOff, LogIn } from 'lucide-react';
import { authAPI } from '../../services/api';
import { clearAuthStorage, getStoredToken, persistAuthSession } from '../../utils/auth';
import { useProductContext } from '../../context/ProductContext';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshProducts } = useProductContext();
    const hasProcessedLogout = useRef(false);
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (hasProcessedLogout.current) return;
        hasProcessedLogout.current = true;
        const logout = async () => {
            if (getStoredToken()) { try { await authAPI.logout(); } catch (_) { } }
            clearAuthStorage();
        };
        logout();
    }, []);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await authAPI.login(form.email, form.password);
            if (res.data.success) {
                persistAuthSession(res.data.data);
                window.showToast('Welcome back!', 'success');
                const user = res.data.data.user;
                // Refresh products/role in context *before* navigating so the
                // dashboard renders with correct state without a hard reload.
                await refreshProducts();
                if (user.mustChangePassword) {
                    navigate('/change-password', { replace: true });
                } else if (user.role === 'PRODUCT_USER') {
                    const assigned = user.assignedProducts || [];
                    if (assigned.length === 1) {
                        // Auto-select and go directly to the dashboard (which shows YouTube analytics)
                        const productId = String(assigned[0]?.id || assigned[0]?._id || assigned[0]);
                        localStorage.setItem('selectedProduct', productId);
                        navigate('/dashboard', { replace: true });
                    } else if (assigned.length > 1) {
                        navigate('/choose-product', { replace: true });
                    } else {
                        navigate('/dashboard', { replace: true });
                    }
                } else {
                    navigate(location.state?.from || '/dashboard', { replace: true });
                }
            }
        } catch (err) {
            window.showToast(err.response?.data?.message || 'Login failed. Please check your credentials.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-logo">
                    <div className="auth-logo-icon"><BarChart2 size={24} /></div>
                </div>
                <h1 className="auth-title">Sign in to Daily Reporting</h1>
                <p className="auth-subtitle">Enter your credentials to access your dashboard</p>
                <form className="auth-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <input type="email" name="email" className="form-input" value={form.email} onChange={handleChange} required autoComplete="email" placeholder="you@company.com" />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Password</label>
                        <div className="password-field">
                            <input type={showPassword ? 'text' : 'password'} name="password" className="form-input" value={form.password} onChange={handleChange} required autoComplete="current-password" placeholder="••••••••" />
                            <button type="button" className="password-toggle" onClick={() => setShowPassword((v) => !v)} aria-label="Toggle password">
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', marginTop: '-0.25rem' }}>
                        <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
                    </div>
                    <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                        {loading ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Signing in…</> : <><LogIn size={16} /> Sign In</>}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Login;
